const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Obtener ciclos escolares
export async function getCycles(institutionId) {
  const url = institutionId 
    ? `${API_BASE}/catalogos/ciclos-escolares/?id_institucion=${institutionId}` 
    : `${API_BASE}/catalogos/ciclos-escolares/`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data)
    ? data.map(c => ({ 
        id: c.id || c.id_ciclo_escolar, 
        id_ciclo_escolar: c.id || c.id_ciclo_escolar,
        name: c.nombre || c.nombre_ciclo,
        nombre_ciclo: c.nombre_ciclo,
        anio: c.anio,
        numero_periodo: c.numero_periodo,
        fecha_inicio: c.fecha_inicio,
        fecha_fin: c.fecha_fin,
        fecha_limite_inscripciones: c.fecha_limite_inscripciones,
        fecha_limite_reinscripciones: c.fecha_limite_reinscripciones,
        fecha_inicio_clases: c.fecha_inicio_clases,
        fecha_fin_clases: c.fecha_fin_clases,
        fecha_captura_parcial1: c.fecha_captura_parcial1,
        fecha_captura_parcial2: c.fecha_captura_parcial2,
        fecha_captura_parcial3: c.fecha_captura_parcial3,
        estado_ciclo: c.estado_ciclo,
        estado: c.estado,
        activo: c.activo,
        // Usar los campos de salida para IDs de FK
        id_institucion: c.id_institucion_out || c.id_institucion,
        id_tipo_periodo: c.id_tipo_periodo_out || c.id_tipo_periodo,
        id_campus: c.id_campus || c.id_campus_out || null,
        institucion_nombre: c.institucion_nombre,
        tipo_periodo_nombre: c.tipo_periodo_nombre
      }))
    : [];
}

// Crear ciclo escolar
export async function createCycle(form) {
  // Limpieza de fechas: si es string vacío, poner null
  const cleanDate = (v) => (v && typeof v === 'string' && v.trim() !== '' ? v : null);
  const payload = {
    id_institucion: form.institucion ? Number(form.institucion) : null,
    id_tipo_periodo: form.tipoPeriodo ? Number(form.tipoPeriodo) : null,
    id_campus: form.id_campus ? Number(form.id_campus) : null,
    anio: form.anio ? parseInt(form.anio) : new Date().getFullYear(),
    numero_periodo: form.periodo ? parseInt(form.periodo) : 1,
    nombre_ciclo: form.nombre_ciclo || `${form.anio || new Date().getFullYear()}-${form.periodo || 1}`,
    fecha_inicio: cleanDate(form.inicio),
    fecha_fin: cleanDate(form.fin),
    fecha_limite_inscripciones: cleanDate(form.inscripcion) || cleanDate(form.inicio),
    fecha_limite_reinscripciones: cleanDate(form.reinscripcion) || cleanDate(form.inicio),
    fecha_inicio_clases: cleanDate(form.inicio),
    fecha_fin_clases: cleanDate(form.fin),
    fecha_captura_parcial1: cleanDate(form.califParcial1),
    fecha_captura_parcial2: cleanDate(form.califParcial2),
    fecha_captura_parcial3: cleanDate(form.examenExtra),
    estado_ciclo: form.estado_ciclo || 'PLANEADO',
    activo: form.estado === 'ACTIVO'
  };
  
  const res = await fetch(`${API_BASE}/catalogos/ciclos-escolares/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  
  if (!res.ok) {
    const rawBody = await res.text().catch(() => '');
    let errorJson = null;
    try {
      errorJson = rawBody ? JSON.parse(rawBody) : null;
    } catch (_) {
      errorJson = null;
    }
    const errorText = rawBody;
    console.error('Error del servidor:', errorJson || errorText || res.statusText);

    let message = 'Error al crear ciclo escolar';
    if (errorJson && typeof errorJson === 'object') {
      const firstKey = Object.keys(errorJson)[0];
      const firstValue = firstKey ? errorJson[firstKey] : null;
      if (Array.isArray(firstValue) && firstValue.length > 0) {
        message = String(firstValue[0]);
      } else if (typeof firstValue === 'string') {
        message = firstValue;
      } else if (typeof errorJson.detail === 'string') {
        message = errorJson.detail;
      }
    } else if (errorText) {
      message = errorText;
    }

    throw new Error(message);
  }
  
  return await res.json();
}

// Actualizar ciclo escolar
export async function updateCycle(id, form) {
  const payload = {
    id_institucion: form.institucion ? Number(form.institucion) : null,
    id_tipo_periodo: form.tipoPeriodo ? Number(form.tipoPeriodo) : null,
    id_campus: form.id_campus ? Number(form.id_campus) : (form.campus ? Number(form.campus) : null),
    anio: form.anio ? parseInt(form.anio) : new Date().getFullYear(),
    numero_periodo: form.periodo ? parseInt(form.periodo) : 1,
    nombre_ciclo: form.nombre || `${form.anio || new Date().getFullYear()}-${form.periodo || 1}`,
    fecha_inicio: form.inicio || null,
    fecha_fin: form.fin || null,
    fecha_limite_inscripciones: form.inscripcion || form.inicio,
    fecha_limite_reinscripciones: form.reinscripcion || form.inicio,
    fecha_inicio_clases: form.inicio || null,
    fecha_fin_clases: form.fin || null,
    fecha_captura_parcial1: form.califParcial1 || null,
    fecha_captura_parcial2: form.califParcial2 || null,
    fecha_captura_parcial3: form.examenExtra || null,
    estado_ciclo: form.estado_ciclo || 'PLANEADO',
    activo: form.estado === 'ACTIVO'
  };
  
  console.log('[ciclosEscolares] Actualizando ciclo:', id, payload);
  
  const res = await fetch(`${API_BASE}/catalogos/ciclos-escolares/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    console.error('[ciclosEscolares] Error del servidor:', error);
    throw new Error('Error al actualizar ciclo escolar');
  }
  return await res.json();
}

// Eliminar ciclo escolar
export async function deleteCycle(id) {
  const res = await fetch(`${API_BASE}/catalogos/ciclos-escolares/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!res.ok) throw new Error('Error al eliminar ciclo escolar');
  return true;
}

